import {
    AssignCategoryMaterial,
    AssignElementMaterial,
    AssignMaterialsRequest,
    AssignNonModelledElementsEditsRequest,
    AssignNonModelledElementsRequest,
    AssignRebarEstimationEditsRequest,
    AssignRebarEstimationsRequest,
    AutoAssignElementMaterial,
    AutoAssignMaterialsRequest,
    FeatureManagementType,
    INonModelledElementsEdit,
    IRebarEstimationEdit,
    Model,
    NonModelledElementsEdit,
    NonModelledElementsStatus,
    Permissions,
    RebarEstimationEdit,
    RebarEstimationStatus,
    RemoveByElement,
    RemoveCategoryMaterial,
    RemoveMaterialsRequest,
    RemoveSupplementalsRequest,
    Scenario,
    ScenarioModel,
    SupplementalValueType,
} from "@api/apiClient";

import { useAppSettings } from "@api/appSettings";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useCo2MaterialTypes } from "@api/co2MaterialTypes";
import {
    useAssignMaterials,
    useAssignNonModelledElements,
    useAssignNonModelledElementsEdits,
    useAssignRebarEstimationEdits,
    useAssignRebarEstimations,
    useAutoAssignMaterials,
    useCategories,
    useComponents,
    useElements,
    useMaterials,
    useModelExtendedData,
    useRemoveMaterials,
    useRemoveSupplementals,
} from "@api/models";
import { useScenarios } from "@api/scenarios";
import MaterialSelectDialog from "@components/MaterialSelectDialog/MaterialSelectDialog";
import VersionChip from "@components/VersionChip/VersionChip";
import { setSelectedNodesKeys } from "@features/analysis/utils/treeGridUtils";
import useAppStore from "@hooks/useAppStore";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import { Button, Grid, ListItemText, ListSubheader, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import isGuid from "@utils/isGuid";
import { observer } from "mobx-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import MuiAssignGrid from "./MuiAssignGrid";

const MuiAssignTab = observer(() => {
  console.log("AssignTab render");
  const { id: projectId } = useParams();
  const appStore = useAppStore();
  const upStore = useUserPermissionStore();
  const [openAssignMaterialDlg, setOpenAssignMaterialDlg] = useState(false);
  const [rebarEstimationStatus, setRebarEstimationStatus] = useState<RebarEstimationStatus | undefined>();
  const scenarios = useScenarios(projectId!);
  const assignMaterialDlgRef = useRef<any>();
  const assignGridApiRef = useGridApiRef();
  const appSettings = useAppSettings();
  const co2MaterialTypes = useCo2MaterialTypes();
  const cadModelCategories = useCadModelCategories();

  // TODO: this can be handled by useScenarioModel by having useScenarioModel return a refetch.
  const categories = useCategories(appStore.model?.modelId, appStore.scenario?.scenarioId, { enabled: false });
  const elements = useElements(appStore.model?.modelId, appStore.scenario?.scenarioId, { enabled: false });
  const components = useComponents(appStore.model?.modelId, appStore.scenario?.scenarioId, { enabled: false });
  const materials = useMaterials(appStore.model?.modelId, appStore.scenario?.scenarioId, { enabled: false });
  const modelExtendedData = useModelExtendedData(appStore.model?.modelId, appStore.scenario?.scenarioId, { enabled: false });
  // TODO: this can be handled by useScenarioModel by having useScenarioModel return a refetch.

  const mutateAssignMaterials = useAssignMaterials(appStore.model?.modelId);
  const mutateAutoAssignMaterials = useAutoAssignMaterials(appStore.model?.modelId);
  const mutateAssignRebarEstimations = useAssignRebarEstimations(appStore.model?.modelId);
  const mutateAssignRebarEstimationEdits = useAssignRebarEstimationEdits(appStore.model?.modelId);
  const mutateAssignNonModelledElements = useAssignNonModelledElements(appStore.model?.modelId);
  const mutateAssignNonModelledElementsEdits = useAssignNonModelledElementsEdits(appStore.model?.modelId);
  const mutateRemoveMaterials = useRemoveMaterials(appStore.model?.modelId);
  const mutateRemoveSupplementals = useRemoveSupplementals(appStore.model?.modelId);

  const isFetching = scenarios.isFetching || categories.isFetching || elements.isFetching || components.isFetching || materials.isFetching;
  const isMutating =
    mutateAssignMaterials.isPending ||
    mutateAutoAssignMaterials.isPending ||
    mutateAssignRebarEstimations.isPending ||
    mutateAssignRebarEstimationEdits.isPending ||
    mutateAssignNonModelledElements.isPending ||
    mutateAssignNonModelledElementsEdits.isPending ||
    mutateRemoveMaterials.isPending ||
    mutateRemoveSupplementals.isPending;
  const isBusy = isFetching || isMutating;

  const finalScenarioTypeId = useMemo(() => {
    const scenarioTypeEntities = appStore.applicationSettings?.metadataEntities?.filter(
      (entity) => entity.type === "ScenarioType",
    );
    return scenarioTypeEntities?.find((entity) => entity.name?.toLowerCase() === "final")?.metadataEntityId;
  }, [appStore.applicationSettings?.metadataEntities]);

  const isScenarioFinal =
    (finalScenarioTypeId && appStore.scenario?.scenarioTypeId === finalScenarioTypeId) ||
    appStore.scenario?.scenarioTypeEntity?.name?.toLowerCase() === "final";

  const getSelectedNodes = () => {
    setSelectedNodesKeys(assignGridApiRef, cadModelCategories.data, appStore);
  };

  const handleScenarioChange = async (event: any) => {
    const scenario = scenarios.data?.find((s) => s.scenarioId === event.target.value);
    if (scenario && scenario !== appStore.scenario) {
      appStore.setModel(null);
      await appStore.selectionManager.unSelectAllModels(appStore.scenario?.scenarioId!);

      appStore.setSelectedNodesKeys(null);
      await appStore.selectionManager.selectScenario(scenario.scenarioId!);
      appStore.setScenario(scenario);

      // TODO: move this to the api layer so we only have to call it in one place
      await Promise.all([
        appStore.queryClient.resetQueries({ queryKey: ["categories"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["elements"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["components"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["materials"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["modelExtendedData"], exact: false }),
      ]);

      appStore.setAssignNodes(null);
    }
  };

  const selectModel = async (model: Model | undefined) => {
    if (model) {
      appStore.setSelectedNodesKeys(null);
      await appStore.selectionManager.selectModels(appStore.scenario?.scenarioId!, model.apsViewId!);
      appStore.setModel(model);

      await Promise.all([
        appStore.queryClient.resetQueries({ queryKey: ["categories"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["elements"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["components"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["materials"], exact: false }),
        appStore.queryClient.resetQueries({ queryKey: ["modelExtendedData"], exact: false }),
      ]);

      // TODO: isn't this handled by useScenarioModel? useScenarioModel can return a refetch.
      await Promise.all([materials.refetch(), categories.refetch(), elements.refetch(), components.refetch(), modelExtendedData.refetch()]);

      appStore.setAssignNodes(null);
    }
  };

  const handleModelChange = (event: any) => {
    const model = appStore.project?.models!.find((m: Model) => m.modelId === event.target.value);
    if (model && model !== appStore.model) {
      selectModel(model);
    }
  };

  const handleAutoAssignMaterials = async () => {
    getSelectedNodes();

    const selectedModelCategories = { ...appStore.selectedModelCategories };

    const categories = Object.keys(selectedModelCategories).map((key: string) => {
      return new AssignCategoryMaterial({
        scenarioId: appStore.scenario?.scenarioId,
        cadModelCategoryId: appStore.selectedModelCategories[key].data.cadModelCategoryId,
      });
    });

    const elements = Object.keys(appStore.selectedNodesKeys)
      .filter((key: string) => isGuid(key))
      .reduce((acc: any, key: string) => {
        // Ensure the key is not a cadModelCategoryId
        if (!Object.hasOwnProperty.call(appStore.selectedModelCategories, key)) {
          // If the cadModelCategoryId of the node exists then it means the category is fully selected
          // and we do not need to process the individual elements.
          if (!Object.hasOwnProperty.call(appStore.selectedModelCategories, appStore.selectedNodesKeys[key].data.cadModelCategoryId)) {
            acc.push(
              new AutoAssignElementMaterial({
                scenarioId: appStore.scenario?.scenarioId,
                elementId: key,
              }),
            );
          }
        }
        return acc;
      }, []);

    const autoAssignMaterialsRequest = new AutoAssignMaterialsRequest({
      modelId: appStore.model?.modelId,
      categories,
      elements,
    });

    await mutateAutoAssignMaterials.mutateAsync(autoAssignMaterialsRequest as any);
    await clearSelectedNodes();
    await refreshAssignNodes();
  };

  const refreshAssignNodes = async () => {
    // TODO: isn't this handled by useScenarioModel? useScenarioModel can return a refetch.

    await Promise.all([
      //appStore.queryClient.resetQueries({ queryKey: ["scenarios"], exact: false }),
      appStore.queryClient.resetQueries({ queryKey: ["categories"], exact: false }),
      appStore.queryClient.resetQueries({ queryKey: ["elements"], exact: false }),
      appStore.queryClient.resetQueries({ queryKey: ["components"], exact: false }),
      appStore.queryClient.resetQueries({ queryKey: ["materials"], exact: false }),
    ]);

    appStore.setAssignNodes(null);

    await Promise.all([materials.refetch(), categories.refetch(), elements.refetch(), components.refetch()]);

    scenarios.refetch();
  };

  const clearSelectedNodes = async () => {
    assignGridApiRef.current.setRowSelectionModel([]);
  };

  const handleAssignMaterialDlg = async () => {
    getSelectedNodes();
    setOpenAssignMaterialDlg(true);
  };

  const handleAssignRebarEstimations = async () => {
    if (appStore.hasRebarPercentEdits) {
      const edits = Object.entries(appStore.rebarPercentEdits).map(([elementId, value]) => {
        return new RebarEstimationEdit({
          modelId: appStore.model?.modelId,
          scenarioId: appStore.scenario?.scenarioId,
          elementId,
          value,
          valueType: SupplementalValueType.Percent,
        } as IRebarEstimationEdit);
      });
      const assignRebarEstimationEditsRequest = new AssignRebarEstimationEditsRequest({ edits });
      appStore.resetRebarModelEdits();

      await mutateAssignRebarEstimationEdits.mutateAsync(assignRebarEstimationEditsRequest as any);
    } else {
      const assignRebarEstimationsRequest = new AssignRebarEstimationsRequest({
        projectId,
        modelId: appStore.model?.modelId,
        scenarioId: appStore.scenario?.scenarioId,
        //profileId: appStore.scenario?.profileId
      });
      await mutateAssignRebarEstimations.mutateAsync(assignRebarEstimationsRequest as any);
    }
    await refreshAssignNodes();
  };

  const handleAssignNonModelledElements = async () => {
    if (appStore.hasNonModelledPercentEdits) {
      const edits = Object.entries(appStore.nonModelledPercentEdits).map(([elementId, value]) => {
        return new NonModelledElementsEdit({
          modelId: appStore.model?.modelId,
          scenarioId: appStore.scenario?.scenarioId,
          elementId,
          value,
          valueType: SupplementalValueType.Percent,
        } as INonModelledElementsEdit);
      });
      const assignNonModelledElementsEditsRequest = new AssignNonModelledElementsEditsRequest({ edits });
      appStore.resetNonModelledModelEdits();

      await mutateAssignNonModelledElementsEdits.mutateAsync(assignNonModelledElementsEditsRequest as any);
    } else {
      const assignNonModelledElementsRequest = new AssignNonModelledElementsRequest({
        projectId,
        modelId: appStore.model?.modelId,
        scenarioId: appStore.scenario?.scenarioId,
      });
      await mutateAssignNonModelledElements.mutateAsync(assignNonModelledElementsRequest as any);
    }
    await refreshAssignNodes();
  };

  const handleAssignMaterials = async (selectedCo2MaterialType?: any) => {
    if (selectedCo2MaterialType) {
      const selectedModelCategories = { ...appStore.selectedModelCategories };

      const categories = Object.keys(selectedModelCategories).map((key: string) => {
        return new AssignCategoryMaterial({
          scenarioId: appStore.scenario?.scenarioId,
          cadModelCategoryId: appStore.selectedModelCategories[key].data.cadModelCategoryId,
          //co2MaterialTypeId: formData.co2MaterialTypeId,
        });
      });

      const elements = Object.keys(appStore.selectedNodesKeys)
        .filter((key: string) => isGuid(key))
        .reduce((acc: any, key: string) => {
          if (!Object.hasOwnProperty.call(appStore.selectedModelCategories, key)) {
            // If the cadModelCategoryId of the node exists then it means the category is fully selected
            // and we do not need to process the individual elements.
            if (!Object.hasOwnProperty.call(appStore.selectedModelCategories, appStore.selectedNodesKeys[key].data.cadModelCategoryId)) {
              acc.push(
                new AssignElementMaterial({
                  scenarioId: appStore.scenario?.scenarioId,
                  elementId: key,
                  //co2MaterialTypeId: formData.co2MaterialTypeId,
                }),
              );
            }
          }
          return acc;
        }, []);

      if (categories.length > 0 || elements.length > 0) {
        const assignMaterialsRequest = new AssignMaterialsRequest({
          modelId: appStore.model?.modelId,
          co2MaterialTypeId: selectedCo2MaterialType.co2MaterialTypeId,
          co2MaterialLibraryId: selectedCo2MaterialType?.co2MaterialLibraryId,
          materialCategory: selectedCo2MaterialType?.category,
          externalId: selectedCo2MaterialType?.externalId,
          name: selectedCo2MaterialType?.name,
          sourceData: JSON.stringify(selectedCo2MaterialType),
          categories,
          elements,
        });

        await mutateAssignMaterials.mutateAsync(assignMaterialsRequest as any);

        if (co2MaterialTypes.data?.find((type: any) => type.co2MaterialTypeId === selectedCo2MaterialType.co2MaterialTypeId) == null) {
          await co2MaterialTypes.refetch();
        }

        await clearSelectedNodes();
        await refreshAssignNodes();
      }
    }

    assignMaterialDlgRef.current?.setIsLoading(false);
    setOpenAssignMaterialDlg(false);
  };

  const handleRemoveMaterials = async () => {
    getSelectedNodes();

    const selectedModelCategories = { ...appStore.selectedModelCategories };

    const categories = Object.keys(selectedModelCategories).map((key: string) => {
      return new RemoveCategoryMaterial({
        scenarioId: appStore.scenario?.scenarioId,
        cadModelCategoryId: appStore.selectedModelCategories[key].data.cadModelCategoryId,
      });
    });

    const elements = Object.keys(appStore.selectedNodesKeys)
      .filter((key: string) => isGuid(key))
      .reduce((acc: any, key: string) => {
        if (!Object.hasOwnProperty.call(appStore.selectedModelCategories, key)) {
          acc.push(
            new RemoveByElement({
              scenarioId: appStore.scenario?.scenarioId,
              elementId: key,
            }),
          );
        }
        return acc;
      }, []);

    const removeSupplementalsRequest = new RemoveSupplementalsRequest({
      modelId: appStore.model?.modelId,
      categories,
      elements,
    });

    await mutateRemoveSupplementals.mutateAsync(removeSupplementalsRequest as any);

    const removeMaterialsRequest = new RemoveMaterialsRequest({
      modelId: appStore.model?.modelId,
      categories,
      elements,
    });

    await mutateRemoveMaterials.mutateAsync(removeMaterialsRequest as any);

    await clearSelectedNodes();
    await refreshAssignNodes();
  };

  useEffect(() => {
    const go = async () => {
      await appStore.selectionManager.setMode("SingleScenarioModel");
      if (appStore.scenario && appStore.model) {
        selectModel(appStore.model);
      }
    };
    go();
  }, []);

  useEffect(() => {
    if (appStore.model && appStore.scenario) {
      const scenarioModel = appStore.scenario.scenarioModels?.find((sm: ScenarioModel) => sm.modelId === appStore.model?.modelId);
      setRebarEstimationStatus(scenarioModel?.rebarEstimationStatus);
    }
  }, [appStore.model]);

  return (
    <Box mt={1} pr={1} sx={{ position: "relative", display: "flex", flexDirection: "column", width: "100%", minHeight: "100%" }}>
      <Box sx={{ float: "left", position: "absolute", top: -32, right: 4 }}>
        {rebarEstimationStatus === RebarEstimationStatus.Enabled && <Typography variant="body2">{i18n.t("results-tab.title.rebar-estimation-enabled")}</Typography>}
        {rebarEstimationStatus === RebarEstimationStatus.Estimated && <Typography variant="body2">{i18n.t("results-tab.title.rebar-estimation-status")}</Typography>}
      </Box>
      <Stack direction={"row"} spacing={2}>
        <Grid container spacing={2} sx={{ width: "100%" }}>
          <Grid size={4}>
            <FormControl fullWidth>
              <InputLabel id="scenario-label">{i18n.t("assign-tab.scenarios-select.title")}</InputLabel>
              <Select
                labelId="scenario-label"
                id="senario-id"
                value={appStore.scenario?.scenarioId || ""}
                label={i18n.t("assign-tab.scenarios-select.title")}
                onChange={handleScenarioChange}
                renderValue={() => {
                  return <Typography>{appStore.scenario?.name}</Typography>;
                }}
              >
                <ListSubheader>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box>{i18n.t("assign-tab.scenarios-list.title")}</Box>
                    <Typography color="text.secondary">{i18n.t("assign-tab.scenarios-list.content")}</Typography>
                  </Box>
                </ListSubheader>
                {scenarios.data?.map((scenario: Scenario) => (
                  <MenuItem key={scenario.scenarioId} value={scenario.scenarioId}>
                    <ListItemText
                      sx={{
                        "& .MuiListItemText-primary": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        },
                      }}
                    >
                      <Box>{scenario.name}</Box>
                      <Box color="text.secondary">{i18n.t("percent", { value: scenario.getPercentageAssigned() })}</Box>
                    </ListItemText>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={8}>
            <FormControl fullWidth>
              <InputLabel id="model-label">{i18n.t("assign-tab.models-select.title")}</InputLabel>
              <Select
                labelId="model-label"
                id="model-id"
                value={appStore.model?.modelId || ""}
                label={i18n.t("assign-tab.models-select.title")}
                onChange={handleModelChange}
                renderValue={(selected) => {
                  const selectedItem = appStore.scenario?.scenarioModels?.find((model) => model.modelId === selected);
                  if (selectedItem) {
                    return (
                      <Grid container alignItems="center" justifyContent="space-between">
                        <Grid size="grow">
                          <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{selectedItem.model?.modelName}</Typography>
                        </Grid>
                        <Grid sx={{ width: 80 }} textAlign="center">
                          <VersionChip label={selectedItem.model?.modelVersion || ""} />
                        </Grid>
                        <Grid sx={{ width: 100 }} textAlign="right">
                          <Typography color="text.secondary">{i18n.t("percent", { value: Number(selectedItem.percentageAssign || 0) })}</Typography>
                        </Grid>
                      </Grid>
                    );
                  }
                  return <Typography>{i18n.t("assign-tab.models-select.title")}</Typography>;
                }}
              >
                <ListSubheader>
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid size="grow">
                      <Box>{i18n.t("assign-tab.models-select.title")}</Box>
                    </Grid>
                    <Grid sx={{ width: 80 }} textAlign="center">
                      <Box color="text.secondary">{i18n.t("assign-tab.version.title")}</Box>
                    </Grid>
                    <Grid sx={{ width: 100 }} textAlign="right">
                      <Box color="text.secondary">{i18n.t("assign-tab.models-select.content")}</Box>
                    </Grid>
                  </Grid>
                </ListSubheader>
                {appStore.scenario?.scenarioModels?.map((scenarioModel) => (
                  <MenuItem key={scenarioModel.modelId} value={scenarioModel.modelId}>
                    <Grid container alignItems="center" width="100%">
                      <Grid size="grow">
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "wrap", // Allow wrapping
                          }}
                        >
                          {scenarioModel.model?.modelName}
                        </Typography>
                      </Grid>
                      <Grid sx={{ width: 80 }} textAlign="center">
                        <VersionChip label={scenarioModel.model?.modelVersion || ""} />
                      </Grid>
                      <Grid sx={{ width: 100 }} textAlign="right">
                        <Typography color="text.secondary">{i18n.t("percent", { value: Number(scenarioModel.percentageAssign || 0) })}</Typography>
                      </Grid>
                    </Grid>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Stack>
      <Box mt={2} sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
        {appStore.scenario && appStore.model && (
          <>
            <MuiAssignGrid modelId={appStore.model?.modelId} apiRef={assignGridApiRef} />
            <Grid my={1} container spacing={1} sx={{ justifyContent: "end" }}>
              <Grid>
                <Button
                  disabled={
                    isBusy ||
                    isScenarioFinal ||
                    Object.keys(appStore.selectedNodesKeys).length === 0 ||
                    !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)
                  }
                  loading={mutateAutoAssignMaterials.isPending}
                  variant="contained"
                  onClick={handleAutoAssignMaterials}
                >
                  {i18n.t("assign-tab.auto-assign-button.title")}
                </Button>
              </Grid>
              <Grid>
                <Button
                  disabled={
                    isBusy ||
                    isScenarioFinal ||
                    Object.keys(appStore.selectedNodesKeys).length === 0 ||
                    !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)
                  }
                  loading={mutateAssignMaterials.isPending}
                  variant="contained"
                  onClick={handleAssignMaterialDlg}
                >
                  {i18n.t("assign-tab.assign-button.title")}
                </Button>
              </Grid>
              {!appSettings.data?.profileLevelRebarSettings && appSettings.data?.features?.includes(FeatureManagementType.RebarEstimation) &&
                (appStore.scenario?.rebarEstimationStatus === RebarEstimationStatus.Enabled || appStore.scenario?.rebarEstimationStatus === RebarEstimationStatus.Estimated) && (
                  <Grid>
                    <Button
                      loading={mutateAssignRebarEstimations.isPending || mutateAssignRebarEstimationEdits.isPending}
                      variant="contained"
                      color={appStore.hasRebarPercentEdits ? "secondary" : "primary"}
                      onClick={handleAssignRebarEstimations}
                      disabled={isBusy || isScenarioFinal || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)}
                    >
                      {i18n.t("assign-tab.rebar-estimation-button.title")}
                    </Button>
                  </Grid>
                )}

                {appSettings.data?.profileLevelRebarSettings && 
                
                (appStore.scenario?.profile?.rebarEstimationStatus === RebarEstimationStatus.Enabled) &&
                 (
                  <Grid>
                    <Button
                      loading={mutateAssignRebarEstimations.isPending}
                      variant="contained"
                      color={appStore.hasRebarPercentEdits ? "secondary" : "primary"}
                      onClick={handleAssignRebarEstimations}
                      disabled={isBusy || isScenarioFinal || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)}
                    >
                      {i18n.t("assign-tab.rebar-estimation-button.title")}
                    </Button>
                  </Grid>
                )}

              {(appStore.scenario?.nonModelledElementsStatus === NonModelledElementsStatus.Enabled ||
                appStore.scenario?.nonModelledElementsStatus === NonModelledElementsStatus.Estimated) && (
                  <Grid>
                    <Button
                      loading={mutateAssignNonModelledElements.isPending ||mutateAssignNonModelledElementsEdits.isPending}
                      variant="contained"
                      color={appStore.hasNonModelledPercentEdits ? "secondary" : "primary"}
                      onClick={handleAssignNonModelledElements}
                      disabled={isBusy || isScenarioFinal || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Edit)
                      }
                    >
                      {i18n.t("assign-tab.non-modelled-elements-button.title")}
                    </Button>
                  </Grid>
                )}
              <Grid>
                <Button
                  loading={mutateRemoveMaterials.isPending}
                  disabled={isBusy || Object.keys(appStore.selectedNodesKeys).length === 0 || !upStore.hasPermission(Permissions.App_Project_Model_Materials_Delete)}
                  variant="outlined"
                  onClick={handleRemoveMaterials}
                >
                  {i18n.t("assign-tab.remove-button.title")}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
        {openAssignMaterialDlg && (
          <MaterialSelectDialog
            ref={assignMaterialDlgRef}
            showMetrics
            showCompare={true}
            open={openAssignMaterialDlg}
            onClose={() => handleAssignMaterials()}
            onSubmit={(selectedCo2MaterialType: any) => handleAssignMaterials(selectedCo2MaterialType)}
          />
        )}
      </Box>
    </Box>
  );
});

export default MuiAssignTab;
